import React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as DeleteOutlineIcon } from "../../../../assets/DeleteSvg.svg";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  useFormContext,
  Controller,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Divider } from "antd";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ReactComponent as UptimeSvg } from "../../../../assets/uptimeSvg.svg";
import { ReactComponent as EquipmentPhotoWrapper } from "../../../../assets/EquipmentPhotoWrapper.svg";
import QRRCode from "react-qr-code";
import { Add } from "@mui/icons-material";
import { ReactComponent as DeltaTechSvg } from "../../../../assets/DeltaTech.svg";

/* ----------------- Dummies (swap to API later) ----------------- */
const PRODUCT_TYPES = [
  "MachineBuilder RM 40K",
  "MachineBuilder RM 60K",
  "CNC Pro 3000",
  "LaserCut X2",
];

const YEARS = Array.from({ length: 40 }, (_, i) => `${new Date().getFullYear() - i}`);

const makeDefaultMachine = () => ({
  productType: "",
  year: "",
  // Composite select (JSON string) -> also sets the two fields below
  locationArea: "",
  locationIndex: "",   // derived from locationArea
  bereichName: "",     // derived from locationArea
  serialNumber: "",
  softwareName: "",
  softwareVersion: "",
  controlType: "",
  controlManufacturer: "",
  previewUrl: "",
  stickerCode: "",
});

export default function StepThree() {
  const { control, getValues, setValue } = useFormContext();

  // Machines array (namespaced)
  const { fields, append, remove } = useFieldArray({
    control,
    name: "step_three.machines",
  });

  // Locations from step 2
  const locations = useWatch({ control, name: "step_two.locations" }) || [];

  // Build options: "Standort — (kein Bereich)" + each Bereich name
  const locationAreaOptions = React.useMemo(() => {
    const opts = [];
    locations.forEach((loc, idx) => {
      const locName = loc?.name || `Standort ${idx + 1}`;
      const bereiche = (loc?.bereiche || []).map((b) => b?.name).filter(Boolean);
      // no Bereich
      opts.push({
        value: JSON.stringify({ locationIndex: idx, bereichName: "" }),
        label: `${locName} — (kein Bereich)`,
      });
      bereiche.forEach((bn) =>
        opts.push({
          value: JSON.stringify({ locationIndex: idx, bereichName: bn }),
          label: `${locName} — ${bn}`,
        })
      );
    });
    return opts;
  }, [locations]);

  // which machine is expanded
  const [expanded, setExpanded] = React.useState(0);

  // ensure one empty machine initially
  React.useEffect(() => {
    if (fields.length === 0) {
      append(makeDefaultMachine());
      setExpanded(0);
    }
  }, []);

  // current expanded machine (for gating)
  const current = useWatch({ control, name: `step_three.machines.${expanded}` });

  // gate Add button
  const isCurrentValid = React.useMemo(() => {
    if (!current) return false;
    const { productType, year, serialNumber, locationArea } = current;
    if (!productType?.trim()) return false;
    if (!year?.trim()) return false;
    if (!serialNumber?.trim()) return false;
    if (!locationArea?.trim()) return false;
    return true;
  }, [current]);

  const handleAddMachine = () => {
    const next = fields.length;
    append(makeDefaultMachine());
    setExpanded(next);
  };

  return (
    <Box className="step3">
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack sx={{ mb: 2 }}>

          <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
            <DeltaTechSvg /> {getValues("step_one.customerName") || "Company Name"}

          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
            Maschinen
          </Typography>
          <Typography className="textSecondary" sx={{ fontWeight: 500 }}>
            Maschinen basieren auf den von Ihnen angelegten Produkttypen.
          </Typography>
        </Stack>

        {fields.map((field, idx) => (
          <Accordion
            key={field.id}
            expanded={expanded === idx}
            onChange={(_, open) => setExpanded(open ? idx : -1)}
            disableGutters
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
                  {getValues(`step_three.machines.${idx}.productType`)?.trim() ||
                    "Neue Maschine"}
                </Typography>
                <IconButton
                  aria-label="Entfernen"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(idx);
                    if (fields.length - 1 <= 1) setExpanded(0);
                    else setExpanded((prev) => Math.max(0, prev - 1));
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </IconButton>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: "8px 168px 16px 16px" }}>
              <Grid container spacing={4}>
                {/* Produkttyp (required) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.productType`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Produkttyp"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        {PRODUCT_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Baujahr (required) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.year`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Baujahr"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        {YEARS.map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Standort und Bereich (composite, required) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.locationArea`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Standort und Bereich"
                        error={
                          !!fieldState.error ||
                          (!locations?.length && expanded === idx)
                        }
                        helperText={
                          fieldState.error
                            ? fieldState.error.message
                            : locations?.length
                              ? ""
                              : "Bitte zuerst Standorte in Schritt 2 anlegen."
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val); // set composite
                          // also populate locationIndex + bereichName
                          try {
                            const { locationIndex, bereichName } = JSON.parse(val);
                            setValue(
                              `step_three.machines.${idx}.locationIndex`,
                              locationIndex,
                              { shouldDirty: true }
                            );
                            setValue(
                              `step_three.machines.${idx}.bereichName`,
                              bereichName || "",
                              { shouldDirty: true }
                            );
                          } catch {
                            setValue(
                              `step_three.machines.${idx}.locationIndex`,
                              "",
                              { shouldDirty: true }
                            );
                            setValue(
                              `step_three.machines.${idx}.bereichName`,
                              "",
                              { shouldDirty: true }
                            );
                          }
                        }}
                      >
                        {locationAreaOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {/* hidden fields kept in form state */}
                  <Controller
                    name={`step_three.machines.${idx}.locationIndex`}
                    control={control}
                    render={() => null}
                  />
                  <Controller
                    name={`step_three.machines.${idx}.bereichName`}
                    control={control}
                    render={() => null}
                  />
                </Grid>

                {/* Hersteller-Seriennummer (required) */}
                <Grid item xs={12} md={6}  mb={3}>
                  <Controller
                    name={`step_three.machines.${idx}.serialNumber`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Hersteller-Seriennummer"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>

                  <Divider orientation="horizontal" className="antdDivider" />

                {/* Software-Bezeichnung (optional) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.softwareName`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Software-Bezeichnung" />
                    )}
                  />
                </Grid>

                {/* Software-Version (optional) */}
                <Grid item xs={12} md={6}  >
                  <Controller
                    name={`step_three.machines.${idx}.softwareVersion`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Software-Version" />
                    )}
                  />
                </Grid>

                {/* Steuerungstyp (optional) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.controlType`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Steuerungstyp" />
                    )}
                  />
                </Grid>

                {/* Steuerungshersteller (optional) */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`step_three.machines.${idx}.controlManufacturer`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Steuerungshersteller" />
                    )}
                  />
                </Grid>

                {/* Platzhalter-Karten */}

                <div className="stickerWrapper" style={{ width: "100%", padding: "36px 0px 24px 36px" }}>
                  <div style={{ width: "50%" }}>
                    <div className="_title">{"Vorschau des Equipments"}</div>
                    <div className="equipmentPlateDiv bxShadow" style={{ width: "100%" }}>
                      <div className="photoWrapper">
                        {<EquipmentPhotoWrapper />
                        }
                      </div>
                      <div className="_contentWrapper">
                        <div className="_border"></div>

                        <div className="_content" style={{ padding: "16px 0px 0px 0px" }}>

                          <Tooltip title={"internerName"}>
                            <div className="eqName1 truncate" style={{ paddingLeft: "16px" }}>
                              {"internerName"}
                            </div>
                          </Tooltip>

                          <div className="eqName2" style={{ paddingLeft: "16px" }}>
                            text1
                          </div>
                          <div className="eqSiteName" style={{ paddingLeft: "16px" }}>
                            text2
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                  <div style={{ width: "50%", marginLeft: "4%" }}>
                    <div className="_title">{"Service-Sticker"}</div>
                    <div className="equipmentPlateDiv bxShadow" style={{ width: "100%" }}>
                      <div className="qrCodeWrapper">
                        {/* <QrCode /> */}

                        <QRRCode
                          size={256}
                          style={{ height: "100%", maxWidth: "100%", width: "100%" }}
                          value={"testing"}
                          viewBox={`0 0 256 256`}
                        />

                      </div>
                      <div className="_contentWrapper">

                        <div className="_content" style={{ padding: "16px 0px 0px 0px" }}>
                          <div className="eqName1">
                            <UptimeSvg />
                          </div>
                          <div className="qrTitle1">
                            Hersteller : {getValues(`step_three.machines.${idx}.serialNumber`) || ""}
                          </div>
                          <div className="qrtitle2">
                            Seriennr:
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="downloadAsPDF">
                      <PDFDownloadLink document={<div></div>} fileName={'-QRCode.pdf'}>
                        {({ loading }) => (loading ? 'Loading document...' : 'Download as PDF')}
                      </PDFDownloadLink>
                    </div>
                  </div>
                </div>

              </Grid>


            </AccordionDetails>
          </Accordion>
        ))}

        <br />
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddMachine}
          disabled={fields.length > 0 && !isCurrentValid}
        >
          Maschine hinzufügen
        </Button>
      </Paper>
    </Box>
  );
}
